import React, {
    createContext,
    useContext,
    useEffect,
    useRef,
    useState,
    useCallback,
} from 'react';

import {
    authFetch,
    getAccessToken,
    setAccessToken,
    clearAccessToken,
    refreshAccessToken as apiRefreshAccessToken,
} from '../Utilities/Api';

import {
    initSocket,
    emitHeartbeat,
    reconnectWithFreshToken,
} from '../socket';

import { normalizeExpiry } from '../Utilities/timeUtils';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [accessTokenExpiry, setAccessTokenExpiry] = useState(null);
    const [loading, setLoading] = useState(true);

    const socketRef = useRef(null);
    const refreshTimer = useRef(null);
    const heartbeatRef = useRef(null);
    const isRefreshing = useRef(false);

    // 🔍 DEBUG: Monitor Global State Changes
    useEffect(() => {
        console.log("📊 [AuthContext] State Update:", {
            loading,
            user: user ? user.username : "null",
            hasToken: !!getAccessToken()
        });
    }, [user, loading]);

    // ---------------------------------------------------------
    // Socket join handler (stable)
    // ---------------------------------------------------------
    const onSocketConnect = useCallback(() => {
        const id = localStorage.getItem('userId');
        console.log("🔌 [Socket] Connected. Joining room:", id);
        if (id && socketRef.current?.connected) {
            socketRef.current.emit('join', id);
        }
    }, []);

    // ---------------------------------------------------------
    // Initial boot
    // ---------------------------------------------------------
    useEffect(() => {
        console.log("🚀 [AuthContext] App Mounted. Starting Init...");

        const init = async () => {
            const token = getAccessToken();
            console.log("🧐 [AuthContext] Init: Token found in memory?", !!token);

            if (token) {
                console.log("✅ [AuthContext] Token exists. Fetching profile...");
                await fetchProfile();
            } else {
                console.log("⚠️ [AuthContext] No token. Attempting refresh...");
                await clientRefreshAndProfile();
            }

            console.log("🏁 [AuthContext] Init sequence finished. Setting loading = false.");
            setLoading(false);
        };

        init();

        return () => {
            console.log("🛑 [AuthContext] Unmounting provider.");
            clearTimeout(refreshTimer.current);
            clearInterval(heartbeatRef.current);

            if (socketRef.current) {
                socketRef.current.off('connect', onSocketConnect);
                socketRef.current.disconnect();
                socketRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ---------------------------------------------------------
    // Token refresh scheduling
    // ---------------------------------------------------------
    useEffect(() => {
        if (!accessTokenExpiry) return;

        clearTimeout(refreshTimer.current);
        const delay = Math.max(0, accessTokenExpiry - Date.now() - 60_000);

        console.log(`⏲️ [AuthContext] Next refresh scheduled in ${(delay / 1000).toFixed(0)}s`);

        refreshTimer.current = setTimeout(clientRefreshAndProfile, delay);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessTokenExpiry]);

    // ---------------------------------------------------------
    // Refresh + profile (mutex protected)
    // ---------------------------------------------------------
    const clientRefreshAndProfile = async () => {
        if (isRefreshing.current) {
            console.warn("⏳ [AuthContext] Refresh already in progress. Skipping.");
            return false;
        }
        isRefreshing.current = true;
        console.log("🔄 [AuthContext] Starting Token Refresh...");

        try {
            const data = await apiRefreshAccessToken();

            if (!data?.accessToken) throw new Error('No token returned from refresh API');

            console.log("✅ [AuthContext] Token Refresh Successful");
            setAccessToken(data.accessToken);
            setAccessTokenExpiry(normalizeExpiry(data.accessTokenExpires));

            reconnectWithFreshToken();
            await fetchProfile();

            return true;
        } catch (err) {
            console.error("❌ [AuthContext] Refresh Failed:", err);
            await logout(true);
            return false;
        } finally {
            isRefreshing.current = false;
        }
    };

    // ---------------------------------------------------------
    // Fetch profile (safe)
    // ---------------------------------------------------------
    const fetchProfile = async () => {
        if (!getAccessToken()) {
            console.warn("⚠️ [AuthContext] fetchProfile called but no token exists.");
            setUser(null);
            return;
        }

        try {
            console.log("📡 [AuthContext] GET /auth/profile...");
            const res = await authFetch('/auth/profile');

            if (res.status === 401) {
                console.warn("⚠️ [AuthContext] Profile 401. Triggering Refresh.");
                await clientRefreshAndProfile();
                return;
            }

            if (!res.ok) throw new Error(`Profile fetch failed: ${res.status}`);

            const profile = await res.json();
            console.log("👤 [AuthContext] Profile Loaded for:", profile.username);

            if (profile?._id) {
                localStorage.setItem('userId', profile._id);
                localStorage.setItem('username', profile.username);
            }

            setUser(profile);
            ensureSocket(profile._id);
        } catch (err) {
            console.error("❌ [AuthContext] Profile Error:", err);
            setUser(null);
        }
    };

    // ---------------------------------------------------------
    // Socket setup (once)
    // ---------------------------------------------------------
    const ensureSocket = (userId) => {
        if (!socketRef.current) {
            console.log("🔌 [Socket] Initializing...");
            socketRef.current = initSocket();
            socketRef.current.on('connect', onSocketConnect);
        }

        if (!socketRef.current.connected) {
            socketRef.current.connect();
        }

        socketRef.current.emit('join', userId);
        startHeartbeat(userId);
    };

    const startHeartbeat = (id) => {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = setInterval(() => emitHeartbeat(id), 10_000);
    };

    const stopHeartbeat = () => {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
    };

    // ---------------------------------------------------------
    // Public API
    // ---------------------------------------------------------
    const login = ({ accessToken, accessTokenExpires, user: payload }) => {
        console.log("🟢 [AuthContext] Login Action Called");
        setAccessToken(accessToken);
        setAccessTokenExpiry(normalizeExpiry(accessTokenExpires));
        setUser(payload);
        setLoading(false);

        if (payload?._id) {
            localStorage.setItem('userId', payload._id);
            localStorage.setItem('username', payload.username);
            ensureSocket(payload._id);
        }
    };

    const logout = async (silent = false) => {
        console.log(`🔴 [AuthContext] Logout Action Called (Silent: ${silent})`);

        // 1. Tell Server
        if (!silent) {
            try {
                await authFetch('/auth/logout', { method: 'POST' });
                console.log("✅ [AuthContext] Server logout success");
            } catch (err) {
                console.warn("⚠️ [AuthContext] Server logout failed", err);
            }
        }

        // 2. Clear Client
        clearAccessToken();
        setUser(null);
        setAccessTokenExpiry(null);
        stopHeartbeat();
        clearTimeout(refreshTimer.current);

        localStorage.removeItem('userId');
        localStorage.removeItem('username');

        if (socketRef.current) {
            socketRef.current.disconnect();
            socketRef.current = null;
        }
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                loading,
                login,
                logout,
                refreshAccessToken: clientRefreshAndProfile,
                refreshProfile: fetchProfile,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuthContext = () => useContext(AuthContext);
export default AuthContext;